/**
 * Routing service implementation. It allows to obtain route between to locations.
 *
 * The tomtom.routing() method creates a new instance of service. Parameters can be passed on creation or provided via
 * convenient functions that can be chained. The method go() performs the actual call.
 *
 * The call is asynchronous therefore user have two options to handle response, either by passing callback function or
 * by using [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise). The
 * SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * The types used in routing service:
 *
 * point - Latitude, longitude pair (in EPSG4326 projection), with the following constraints:  Latitude values must be
 * in the range -90..+90 inclusive. Longitude values must be in the range -180..+180 inclusive. Example:
 * 52.37245,4.89406
 *
 * circle - A circle with a center point and a radius (in meters).
 * The radius must be a positive integer with the maximum value of 20050000.
 * Note that larger integer values will not be rejected but lowered to the maximum value. Example:
 * circle(52.37245,4.89406,10000)
 *
 * location - A point or a circle. Example: 52.37245,4.89406  circle(52.37245,4.89406,10000)
 *
 * dateTime - A date and time specified in RFC-3339 format with an optional time zone offset. Example:
 * 1996-12-19T16:39:57  1996-12-19T16:39:57-08:00
 *
 * @module Services
 * @class routing
 */
define('services/routing',['rest/routing', 'model/routing/routing', './chain', './validators', './converters'],
    function(routing, model, chain, validators, converters) {
        'use strict';

        var LANGUAGE = {
            AfrikaansSouthAfrica: 'ar-ZA',
            Arabic: 'ar',
            Bulgarian: 'bg-BG',
            ChineseTaiwan: 'zh-TW',
            Czech: 'cs-CZ',
            Danish: 'da-DK',
            Dutch: 'nl-NL',
            EnglishGB: 'en-GB',
            EnglishUS: 'en-US',
            Finnish: 'fi-FI',
            French: 'fr-FR',
            German: 'de-DE',
            Greek: 'el-GR',
            Hungarian: 'hu-HU',
            Indonesian: 'id-ID',
            Italian: 'it-IT',
            Korean: 'ko-KR',
            Lithuanian: 'lt-LT',
            Malay: 'ms-MY',
            Norwegian: 'nb-NO',
            Polish: 'pl-PL',
            PortugueseBrazil: 'pt-BR',
            PortuguesePortugal: 'pt-PT',
            Russian: 'ru-RU',
            Slovak: 'sk-SK',
            Slovenian: 'sl-SI',
            Spanish: 'es-ES',
            SpanishMexico: 'es-MX',
            Swedish: 'sv-SE',
            Thai: 'th-TH',
            Turkish: 'tr-TR'
        };

        var fields = {

            /**
             * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
             * tomtom.key or tomtom.routingKey function. But also per service instance on initialization or with this
             * method.
             *
             * @method key
             * @chainable
             * @param {String} key API Key valid for requested service.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or key value if argument
             *     is omitted.
             */
            key: {validator: validators.string},

            /**
             * A setter and getter for 'locations' attribute. Locations through which the route is calculated. The
             * following constraints apply: At least two locations must be provided. The first location in the sequence
             * defines the origin and must be of type point. The last location in the sequence defines the destination
             * and must be of type point. One or more optional intermediate locations (known as waypoints) may be
             * provided: The maximum allowed number of waypoints is 50, reduced to 20 when computeBestOrder is true. A
             * point waypoint results in an extra leg in the response, a circle waypoint does not. Circle waypoints
             * cannot be used when computeBestOrder is true.
             *
             * The points or circles that will be used to calculate the route. Following input types are allowed:
             *  1. string with Colon-delimited locations. For example
             *  2. array containing locations
             *     location can be defined as string,object or array
             *
             * @method locations
             * @chainable
             * @param {String|Array} [locations] This is the specified locations for route calculation.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or converted locations
             *     value if argument is omitted.
             */
            locations: {validator: validators.routingLocations, converter: converters.routingLocations, required: true},

            /**
             * A setter and getter for 'maxAlternatives' attribute.
             * Number of desired alternative routes to be calculated. The value provided:
             * must be an integer in the range 0-5
             * using a value greater than 0 in conjunction with computeBestOrder set to true is not allowed
             * fewer alternative routes may be returned if either fewer alternatives exist or the requested number of
             * alternatives is larger than the service can calculate Default value is 0, Maximum value is 5
             *
             * @method maxAlternatives
             * @chainable
             * @param {Number} [maxAlternatives] To restrict maximum alternative routes to be calculated.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Number} Method returns the service instance allowing chaining or maxAlternatives value
             *     if argument is omitted.
             */
            maxAlternatives: {validator: validators.numberInInterval(0, 5)},

            /**
             * A setter and getter for 'routeType' attribute.
             * The type of route requested. Notes on specific values:
             * fastest returns the fastest route.
             * shortest returns the shortest route by distance.
             * eco routes balance economy and speed.
             * thrilling routes include interesting or challenging roads and use as few motorways as possible. You can
             * choose the level of turns included and also the degree of hilliness. See the hilliness and windingness
             * parameters for how to set this. There is a limit of 900 km on routes planned with routeType=thrilling
             * Default value is fastest
             *
             * @method routeType
             * @chainable
             * @param {String} [routeType] Route type to be used during route calculation.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or route type value if
             *     argument is omitted.
             */
            routeType: {validator: validators.oneOfValue(['fastest', 'shortest', 'eco', 'thrilling'], 'route type')},

            /**
             * A setter and getter for 'traffic' attribute.
             * Possible values:
             * true (do consider all available traffic information during routing)
             * false (ignore current traffic data during routing). Note that although the current traffic data is
             * ignored during routing, the effect of historic traffic on effective road speeds is still incorporated.
             * Default is true
             *
             * @method traffic
             * @chainable
             * @param {String|Boolean} [traffic] Boolean flag determine if traffic data should be used to calculate the
             *     route.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Boolean} Method returns the service instance allowing chaining or traffic value if
             *     argument is omitted.
             */
            traffic: {validator: validators.bool},

            /**
             * A setter and getter for 'avoid' attribute.
             * Specifies something that the route calculation should try to avoid when determining the route. Can be
             * specified multiple times. Possible values: tollRoads avoids toll roads. motorways avoids motorways.
             * ferries avoids ferries. unpavedRoads avoids unpaved roads. carpools avoids routes that require use of
             * carpool (HOV/ High Occupancy Vehicle) lanes. alreadyUsedRoads avoids using the same road multiple times.
             * Most useful in conjunction with routeType=thrilling.
             *
             * @method avoid
             * @chainable
             * @param {Array|String} [avoid] Specifies something that the route calculation should try to avoid when
             *     determining the route
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Array} Method returns the service instance allowing chaining or street number value if
             *     argument is omitted.
             */
            avoid: {
                validator: validators.arrayOfValues(['tollRoads', 'motorways', 'ferries', 'unpavedRoads', 'carpools',
                    'alreadyUsedRoads'], 'avoid'),
                converter: converters.arrayOfStrings
            },

            /**
             * A setter and getter for 'instructionsType' attribute.
             * If specified, guidance instructions will be returned (if available).
             *
             * Possible values:
             * * _coded_ returns raw instruction data without human-readable messages.
             * * _text_ returns raw instructions data with human-readable messages in plain text.
             * * _tagged_ returns raw instruction data with tagged human-readable messages to permit formatting.
             *
             * Note that the instructionsType parameter cannot be used in conjunction with routeRepresentation=none.
             * If alternative routes are requested, instructions will be generated for each route returned.
             *
             * @method instructionsType
             * @chainable
             * @param {String} [instructionsType] If a street number is sent in along with the request, the response
             *     may include the side of the street (Left/Right) and also an offset position for that street number.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or street number value if
             *     argument is omitted.
             */
            instructionsType: {validator: validators.oneOfValue(['coded', 'text', 'tagged'], 'instructions type')},

            /**
             * A setter and getter for 'language' attribute.
             * The language parameter determines the language of the guidance messages. It does not affect proper nouns
             * (the names of streets, plazas, etc.) It has no effect when instructionsType=coded. Allowed values are (a
             * subset of) the IETF language tags described here. The currently supported languages are listed in the
             * supported languages section below.
             *
             * @method language
             * @chainable
             * @param {String} [language] The language value
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            language: {validator: validators.routingGuidanceLanguage, converter: converters.routingGuidanceLanguage},

            /**
             * A setter and getter for 'departAt' attribute.
             * The date and time of departure from the origin point.
             * Departure times apart from now must be specified as a dateTime.
             * When a time zone offset is not specified, it will be assumed to be that of the origin point.
             * The departAt value must be in the future. The departAt parameter cannot be used in conjunction with
             * arriveAt.
             *
             * @method departAt
             * @chainable
             * @param {String} [departAt] The properly formatted date with time or 'now' value. Date cannot be in the
             *     past.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            departAt: {validator: validators.departAt, converter: converters.dateTime},

            /**
             * A setter and getter for 'arriveAt' attribute.
             * The date and time of arrival at the destination point.
             * It must be specified as a dateTime.
             * When a time zone offset is not specified it will be assumed to be that of the destination point.
             * The arriveAt value must be in the future. The arriveAt parameter cannot be used in conjunction with
             * departAt.
             *
             * @method arriveAt
             * @chainable
             * @param {String} [arriveAt] The properly formatted date with time value. Date cannot be in the past.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            arriveAt: {validator: validators.arriveAt, converter: converters.dateTime},

            /**
             * A setter and getter for 'computeBestOrder' attribute. Allows to reorder the route waypoints to reduce
             * the route length. Yields best results when used in conjunction with routeType shortest. Possible values:
             * true (compute a better order, if possible; not allowed to be used in conjunction with maxAlternatives
             * value greater than 0; not allowed to be used in conjunction with circle waypoints). The response will
             * include the optimized waypoint indices. This feature can be used with a maximum of 20 route waypoints.
             * false (use the locations in the given order). Not allowed to be used in conjunction with
             * routeRepresentation none.
             *
             * @method computeBestOrder
             * @chainable
             * @param {String} [computeBestOrder] Flag to reorder the route waypoints to reduce the route length
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            computeBestOrder: {validator: validators.bool},

            /**
             * A setter and getter for 'routeRepresentation' attribute.
             * Specifies the representation of the set of routes provided as response. Possible values:
             * * _polyline_ includes routes in the response.
             * * _none_ includes only the optimized waypoint indices but does not include the routes in the response.
             *
             * This parameter value can only be used in conjunction with computeBestOrder true.
             *
             * @method routeRepresentation
             * @chainable
             * @param {String} [routeRepresentation] Allows the routing response to omit the actual route and give only
             *     the best ordered waypoints.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            routeRepresentation: {validator: validators.oneOfValue(['polyline', 'none'], 'route representation')},

            /**
             * A setter and getter for 'travelMode' attribute.
             * The mode of travel for the requested route. Possible values: _car, truck, taxi, bus, van, motorcycle,
             * bicycle, pedestrian_ Note that the requested travelMode may not be available for the entire route. Where
             * the requested travelMode is not available for a particular section, the <travelMode> element of the
             * response for that section will be other.
             *
             * @method travelMode
             * @chainable
             * @param {String} [travelMode] The primary mean of transportation to be used while routing.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            travelMode: {
                validator: validators.oneOfValue(['car', 'truck', 'taxi', 'bus', 'van', 'motorcycle', 'bicycle',
                    'pedestrian'], 'travel mode')
            },

            /**
             * A setter and getter for 'hilliness' attribute.
             * Degree of hilliness for thrilling route. Possible values: low, normal, high
             * This parameter can only be used in conjunction with routeType thrilling.
             *
             * @method hilliness
             * @chainable
             * @param {String} [hilliness] The level of hilliness on a thrilling route.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            hilliness: {validator: validators.oneOfValue(['low', 'normal', 'high'], 'hilliness')},

            /**
             * A setter and getter for 'windingness' attribute.
             * Level of turns for thrilling route. Possible values: low, normal, high
             * This parameter can only be used in conjunction with routeType thrilling.
             *
             * @method windingness
             * @chainable
             * @param {String} [windingness] The level of windingness on a thrilling route.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            windingness: {validator: validators.oneOfValue(['low', 'normal', 'high'], 'windingness')},

            /**
             * A setter and getter for 'report' attribute.
             * Specifies which data should be reported for diagnosis purposes. Possible values: _effectiveSettings_
             * Reports the effective parameters or data used when calling the API.
             * In the case of defaulted parameters the default will be reflected where the parameter was not specified
             * by the caller.
             *
             * @method report
             * @chainable
             * @param {String} [report] Level of verbose answer from service, useful for debugging purposes.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or language if argument
             *     is omitted.
             */
            report: {validator: validators.oneOfValue(['effectiveSettings'], 'report')},

            /**
             * A setter and getter for 'computeTravelTimeFor' attribute.
             * Specifies whether to return additional travel times using different types of traffic information (none,
             * historic, live) as well as the default best-estimate travel time. Possible values:
             * * none - do not compute additional travel times,
             * * all - compute travel times for all types of traffic information. Specifying all results in the fields
             * noTrafficTravelTimeInSeconds, historicTrafficTravelTimeInSeconds and
             * liveTrafficIncidentsTravelTimeInSeconds being included in the summaries in the route response.
             *
             * @method computeTravelTimeFor
             * @chainable
             * @param {String} [computeTravelTimeFor] Setting _all_ provides additional information regards travel
             *     times.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            computeTravelTimeFor: {validator: validators.oneOfValue(['none', 'all'], 'compute travel time for')},

            /**
             * A setter and getter for 'vehicleEngineType' attribute.
             * Engine type of the vehicle. This parameter is currently only considered for travelMode truck. Valid
             * options are: _combustion, electric_
             *
             * @method vehicleEngineType
             * @chainable
             * @param {String} [vehicleEngineType] The vehicle engine type.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleEngineType: {validator: validators.oneOfValue(['combustion', 'electric'], 'vehicle engine type')},

            /**
             * A setter and getter for 'vehicleMaxSpeed' attribute.
             * Maximum speed of the vehicle in km/hour. A value of 0 means that an appropriate value for the vehicle
             * will be determined and applied during route planning. A non-zero value may be overridden during route
             * planning. This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleMaxSpeed
             * @chainable
             * @param {Integer} [vehicleMaxSpeed] The maximal allowed vehicle speed in km/h.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Integer} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleMaxSpeed: {validator: validators.naturalInteger},

            /**
             * A setter and getter for 'vehicleWeight' attribute.
             * Weight of the vehicle in kg. A value of 0 means that weight restrictions are not considered during route
             * planning. This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleWeight
             * @chainable
             * @param {Integer} [vehicleWeight] The vehicle weight in kg.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Integer} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleWeight: {validator: validators.naturalInteger},

            /**
             * A setter and getter for 'vehicleAxleWeight' attribute.
             * Weight per axle of the vehicle in kg. A value of 0 means that weight restrictions per axle are not
             * considered during route planning. This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleAxleWeight
             * @chainable
             * @param {Integer} [vehicleAxleWeight] The vehicle axle wight in kg.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Integer} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleAxleWeight: {validator: validators.naturalInteger},

            /**
             * A setter and getter for 'vehicleLength' attribute.
             * Length of the vehicle in meters. A value of 0 means that length restrictions are not considered during
             * route planning. This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleLength
             * @chainable
             * @param {Float} [vehicleLength] The vehicle length in m.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Float} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleLength: {validator: validators.numberInInterval(0, Number.MAX_VALUE)},

            /**
             * A setter and getter for 'vehicleWidth' attribute.
             * Width of the vehicle in meters. A value of 0 means that width restrictions are not considered during
             * route planning. This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleWidth
             * @chainable
             * @param {Float} [vehicleWidth] The vehicle width in m.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Float} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleWidth: {validator: validators.numberInInterval(0, Number.MAX_VALUE)},

            /**
             * A setter and getter for 'vehicleHeight' attribute.
             * Height of the vehicle in meters. A value of 0 means that height restrictions are not considered
             * during route planning. This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleHeight
             * @chainable
             * @param {Float} [vehicleHeight] The vehicle height in m.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Float} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleHeight: {validator: validators.numberInInterval(0, Number.MAX_VALUE)},

            /**
             * A setter and getter for 'vehicleCommercial' attribute.
             * Vehicle is used for commercial purposes and thus may not be allowed to drive on some roads. This
             * parameter is currently only considered for travelMode truck.
             *
             * @method vehicleCommercial
             * @chainable
             * @param {Boolean} [vehicleCommercial] True if vehicle is used for commercial purposes.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|String} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleCommercial: {validator: validators.bool},

            /**
             * A setter and getter for 'vehicleHeading' attribute.
             * The directional heading of the vehicle in degrees starting at true North and continuing in clockwise
             * direction. North is 0 degrees, east is 90 degrees, south is 180 degrees, west is 270 degrees. Possible
             * values 0-359.
             *
             * @method vehicleHeading
             * @chainable
             * @param {Integer} [vehicleHeading] The angle in which the vehicle is heading.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Integer} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleHeading: {validator: validators.integerInInterval(-1, 359)},

            /**
             * A setter and getter for 'vehicleLoadType' attribute.
             * Types of cargo that may be classified as hazardous materials and restricted from some roads. Available
             * vehicleLoadType values are US Hazmat classes 1 through 9, plus generic classifications for use in other
             * countries.
             *
             * __Use these for routing in US__
             * * USHazmatClass1 Explosives
             * * USHazmatClass2 Compressed gas
             * * USHazmatClass3 Flammable liquids
             * * USHazmatClass4 Flammable solids
             * * USHazmatClass5 Oxidizers
             * * USHazmatClass6 Poisons
             * * USHazmatClass7 Radioactive
             * * USHazmatclass8 Corrosives
             * * USHazmatClass9 Miscellaneous
             *
             * __Use these for routing in all other countries__
             * * otherHazmatExplosive Explosives
             * * otherHazmatGeneral Miscellaneous
             * * otherHazmatHarmfulToWater Harmful to water
             *
             * This parameter is currently only considered for travelMode truck.
             *
             * @method vehicleLoadType
             * @chainable
             * @param {Array|String} [vehicleLoadType] The array of truck cargo classification.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {routing|Array} Method returns the service instance allowing chaining or current value if
             *     argument is omitted.
             */
            vehicleLoadType: {
                validator: validators.arrayOfValues(['USHazmatClass1', 'USHazmatClass2', 'USHazmatClass3',
                    'USHazmatClass4', 'USHazmatClass5', 'USHazmatClass6', 'USHazmatClass7', 'USHazmatClass8',
                    'USHazmatClass9', 'otherHazmatExplosive', 'otherHazmatGeneral', 'otherHazmatHarmfulToWater'],
                    'vehicle load type'),
                converter: converters.arrayOfStrings
            },

            /**
             * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] Protocol type, one of _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {routing|String} Method returns the service instance allowing chaining or _protocol_
             * value if argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(['http', 'https'], 'protocol')}
        };

        /**
         * The function performing actual call to the web service. This method can be executed multiple times, it is
         * not changing parameters itself.
         *
         * Callback function takes a response parameter and fail callback takes error as only parameter.
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.routingKey(<Your API key>);
         * var routing =
         *     tomtom.routing().locations('52.3679,4.8786:52.3678,4.8797').go(myFunction);
         * ```
         */
        /**
         * A setter and getter for callback function. This function is called after receiving successful response from
         * service. It takes one argument which is the response - JSON described in go() function documentation.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} if the given argument is not a function
         * @return {routing} Method returns the service instance allowing chaining or function if argument is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {routing|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        function handleChain(data, callback) {
            routing.calculateRoute(data, function(response) {
                callback.call(this, model(response));
            });
        }

        /**
         * The method to instantiates the routing service. The options can be passed on creation or later during the
         * execution using function with the name matching parameter name. Each option can be retrieved using the
         * exactly same function but without parameter. The instance of service can be reused by calling multiple times
         * go() function.
         *
         * @method routing
         * @param {Object} [options] Options to be passed to the routing call.
         * @param {String} [options.key=global API key] The API key to be used.
         * @param {String} [options.locations] Locations through which the route is calculated
         * @param {String} [options.contentType] The content type of the response structure
         * @param {String} [options.maxAlternatives] Number of desired alternative routes to be calculated
         * @param {String} [options.routeType] The type of route requested.
         * @param {String} [options.traffic] consider all available traffic information during routing
         * @param {String} [options.avoid] Specifies something that the route calculation should try to avoid when
         *     determining the route.
         * @param {String} [options.instructionsType] If specified, guidance instructions will be returned (if
         *     available)
         * @param {String} [options.language] The language parameter determines the language of the guidance messages
         * @param {String} [options.departAt] The date and time of departure from the origin point
         * @param {String} [options.arriveAt] The date and time of arrival at the destination point
         * @param {String} [options.computeBestOrder] Re-order the route waypoints to reduce the route length
         * @param {String} [options.routeRepresentation] Specifies the representation of the set of routes provided as
         *     response
         * @param {String} [options.travelMode] The mode of travel for the requested route
         * @param {String} [options.hilliness] Degree of hilliness for thrilling route
         * @param {String} [options.windingness] Level of turns for thrilling route
         * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
         *
         * @example
         * ```javascript
         *
         * tomtom.routing().locations('52.3,4.8:52.37,4.87').go().then(function(routeGeoJson) {
         *   console.log(routeGeoJson);
         * });
         * ```
         */
        return function(options) {
            var serviceFunction = chain(fields, handleChain, options);
            serviceFunction.LANGUAGE = LANGUAGE;
            return serviceFunction;
        };
    });

